/**
 * 活动页通用商品列表组件
 * 基于首页商品列表组件改造，提供统一的商品展示体验
 */

'use client';

import { ListProduct } from '@/api/types/public';
import ProductCard from '@/components/Product/ProductCard';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
// @ts-ignore: Icon library missing type definitions
import { ToRightArrow } from '@zeelool/icon';
import './activity-product-list-navigation.css';

interface ActivityProductListProps {
  products: ListProduct[];
  onTrack?: (params: any) => void;
  onExpTrack?: (params: any) => void;
  moduleId?: string | number;
  nameSuffix?: string;
  // 新增布局样式配置
  cardWidth?: string; // 卡片宽度，默认 '!w-[23%]'
  cardMinWidth?: string; // 卡片最小宽度，默认 '!min-w-[328px]'
  cardMargin?: string; // 卡片间距，默认 'mr-[16px]'
  cardPadding?: string; // 容器内边距，默认 'sm:!px-[24px] lg:!px-[48px]'
}

export default function ActivityProductList({
  products,
  onTrack,
  onExpTrack,
  moduleId = 'activity',
  nameSuffix,
  cardWidth = '!w-[23%]',
  cardMinWidth = '!min-w-[328px]',
  cardMargin = 'mr-[16px]',
  cardPadding = 'sm:!px-[24px] xl:!px-[48px]',
}: ActivityProductListProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const swiperRef = useRef<any>(null);

  const isPC = deviceType === EPlatform.WEB;

  // 为每个组件实例生成唯一ID
  const uniqueId = useMemo(
    () => `activity-product-list-${String(moduleId).replace(/[^a-zA-Z0-9-]/g, '-')}`,
    [moduleId],
  );

  // PC端Swiper配置
  const pcSwiperOptions = useMemo(
    () => ({
      modules: [Navigation],
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: 'auto' as const,
      navigation: {
        nextEl: `.${uniqueId}-next`,
        prevEl: `.${uniqueId}-prev`,
      },
    }),
    [uniqueId],
  );

  // 移动端Swiper配置
  const mobileSwiperOptions = useMemo(
    () => ({
      modules: [Navigation],
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: 2.2,
      spaceBetween: 5,
      slidesOffsetBefore: 14,
      slidesOffsetAfter: 14,
    }),
    [],
  );

  const swiperOptions = isPC ? pcSwiperOptions : mobileSwiperOptions;

  // 点击处理
  const handleProductClick = useCallback(
    (index: number, product: ListProduct) => {
      if (onTrack) {
        onTrack({
          product,
          index,
          moduleId,
        });
      }
    },
    [moduleId, onTrack],
  );

  // 曝光处理
  const handleProductExpose = useCallback(
    (index: number, product: ListProduct) => {
      if (onExpTrack) {
        onExpTrack({
          product,
          index,
          moduleId,
        });
      }
    },
    [moduleId, onExpTrack],
  );

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <div className="product-swiper-container relative">
      <Swiper ref={swiperRef} {...swiperOptions} className={`${cardPadding} !pb-[2px]`}>
        {products.map((item, index) => (
          <SwiperSlide
            key={item.id}
            className={`
              swiper-item relative
              ${isPC ? `${cardWidth} ${cardMargin} last:!mr-0 ${cardMinWidth} 2xl:!min-w-[auto]` : ''}
            `}
          >
            <ProductCard
              product={item}
              index={index}
              showReviewStar
              showCorrelation
              showTryOn={false}
              showMember={false}
              showTag={false}
              showOriginalPrice={false}
              showName={false}
              nameSuffix={nameSuffix}
              imageClassName="!aspect-[416/500] !rounded-[0px]"
              imageHoverRule={[]}
              namePriceInline={false}
              handelClick={(cardIndex, product) => {
                handleProductClick(cardIndex, product as ListProduct);
              }}
              onValidExposure={(cardIndex, product) => {
                handleProductExpose(cardIndex, product as ListProduct);
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* PC端导航按钮 */}
      {isPC && (
        <>
          <div
            className={`
              ${uniqueId}-next product-swiper-navigation absolute z-10 flex items-center justify-center
              w-[75px] xl:w-[100px] h-[53px] xl:h-[70px]
              top-[137px] xl:top-[185px] right-[48px] xl:right-[105px]
              rounded-[35px] border-2 border-white text-white cursor-pointer
            `}
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            }}
          >
            <ToRightArrow className="text-[30px] xl:text-[40px]" />
          </div>
          <div
            className={`
              ${uniqueId}-prev product-swiper-navigation absolute z-10 flex items-center justify-center
              w-[75px] xl:w-[100px] h-[53px] xl:h-[70px]
              top-[137px] xl:top-[185px] left-[48px] xl:left-[105px]
              rounded-[35px] border-2 border-white text-white cursor-pointer
            `}
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            }}
          >
            <ToRightArrow className="text-[30px] xl:text-[40px] rotate-180" />
          </div>
        </>
      )}
    </div>
  );
}
